import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectCompanyInfo } from "store/companySlice";
import { selectCurrentBoardData } from "store/currentBoardSlice";

import Api from "api/Api";
import MainContainer from "layout/atoms/MainContainer";
import { useTranslation } from "react-i18next";

import CodeBlock from "layout/atoms/CodeBlock";
import ContentBox from "layout/atoms/ContentBox";
import GridBoard from "layout/atoms/GridBoard";
import Tabs from "layout/atoms/Tabs";
import {
  IntroText,
  StepDescription,
  StepTitle,
  WidgetStep,
} from "./WidgetStyles";

function BoardWidget() {
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const companyInfo = useSelector(selectCompanyInfo);
  const currentBoardData = useSelector(selectCurrentBoardData);
  const api = new Api();
  const { t } = useTranslation();
  const [boardHash, setBoardHash] = useState("");

  useEffect(() => {
    if (boardId) {
      fetchWidgetSetup();
    }
  }, [boardId]);

  const fetchWidgetSetup = async () => {
    await api.getWidgetSetup(boardId).then((widgetSetup) => {
      setBoardHash(widgetSetup.boardHash);
    });
  };

  const scriptCode = `<script src="https://app.ideolo.co/widget/index.js"></script>`;

  const htmlCode = `<div
  id="ideolo-board"
  data-base-url="https://app.ideolo.co/"
  data-board-hash="${boardHash}"
  data-sso-token="**${t("boardsDetails.widget.ssoToken")}**"
></div>`;

  const javaCode = `// Maven dependency
<dependency>
  <groupId>com.auth0</groupId>
  <artifactId>java-jwt</artifactId>
  <version>3.18.3</version>
</dependency>

// Java implementation
import com.auth0.jwt.JWT;
import com.auth0.jwt.algorithms.Algorithm;
import java.util.HashMap;
import java.util.Map;

public class IdeoloSSOTokenGenerator {
    
    private final static String COMPANY_SECRET = "${companyInfo.secret}";
    
    public String generateToken(String userId, String userDisplayedName) {
        Map<String, String> payload = new HashMap<>();
        payload.put("id", userId);
        payload.put("name", userDisplayedName);
        
        Algorithm algorithm = Algorithm.HMAC256(COMPANY_SECRET);
        
        return JWT.create()
            .withPayload(payload)
            .sign(algorithm);
    }
}`;

  const jsCode = `// Install jsonwebtoken package
npm install jsonwebtoken

// JavaScript implementation
const jwt = require('jsonwebtoken');

const COMPANY_SECRET = '${companyInfo.secret}';

function generateToken(userId, userDisplayedName) {
    const payload = {
        id: userId,
        name: userDisplayedName
    };
    
    return jwt.sign(payload, COMPANY_SECRET, { algorithm: 'HS256' });
}

// Usage example
const token = generateToken('user123', 'John Doe');
console.log(token);`;

  const phpCode = `<?php
// Install JWT package
composer require firebase/php-jwt

// PHP implementation
use Firebase\\JWT\\JWT;

class IdeoloSSOTokenGenerator 
{
    private const COMPANY_SECRET = '${companyInfo.secret}';
    
    public function generateToken($userId, $userDisplayedName) 
    {
        $payload = [
            'id' => $userId,
            'name' => $userDisplayedName
        ];
        
        return JWT::encode($payload, self::COMPANY_SECRET, 'HS256');
    }
}

// Usage example
$generator = new IdeoloSSOTokenGenerator();
$token = $generator->generateToken('user123', 'John Doe');
echo $token;
?>`;

  const pythonCode = `# Install JWT package
pip install PyJWT

# Python implementation
import jwt

class IdeoloSSOTokenGenerator:
    COMPANY_SECRET = '${companyInfo.secret}'
    
    def generate_token(self, user_id, user_displayed_name):
        payload = {
            'id': user_id,
            'name': user_displayed_name
        }
        
        return jwt.encode(payload, self.COMPANY_SECRET, algorithm='HS256')

# Usage example
generator = IdeoloSSOTokenGenerator()
token = generator.generate_token('user123', 'John Doe')
print(token)`;

  const csharpCode = `// Install NuGet package: System.IdentityModel.Tokens.Jwt
using Microsoft.IdentityModel.Tokens;
using System.IdentityModel.Tokens.Jwt;
using System.Text;
using System.Collections.Generic;

namespace IdeoloIntegration
{
    public class IdeoloSSOTokenGenerator
    {
        private const string COMPANY_SECRET = "${companyInfo.secret}";
        
        public string GenerateToken(string userId, string userDisplayedName)
        {
            var securityKey = new SymmetricSecurityKey(Encoding.UTF8.GetBytes(COMPANY_SECRET));
            var credentials = new SigningCredentials(securityKey, SecurityAlgorithms.HmacSha256);
            
            var header = new JwtHeader(credentials);
            
            var payload = new JwtPayload
            {
                { "id", userId },
                { "name", userDisplayedName }
            };
            
            var secToken = new JwtSecurityToken(header, payload);
            var handler = new JwtSecurityTokenHandler();
            
            return handler.WriteToken(secToken);
        }
    }
    
    // Usage example
    var generator = new IdeoloSSOTokenGenerator();
    var token = generator.GenerateToken("user123", "John Doe");
    Console.WriteLine(token);
}`;

  const ssoTabs = [
    {
      label: t("boardsDetails.widget.languages.javascript"),
      content: (
        <CodeBlock
          code={jsCode}
          language="javascript"
          title={t("boardsDetails.widget.codeBlockTitles.nodeJs")}
        />
      ),
    },
    {
      label: t("boardsDetails.widget.languages.java"),
      content: (
        <CodeBlock
          code={javaCode}
          language="java"
          title={t("boardsDetails.widget.codeBlockTitles.javaMaven")}
        />
      ),
    },
    {
      label: t("boardsDetails.widget.languages.php"),
      content: (
        <CodeBlock
          code={phpCode}
          language="php"
          title={t("boardsDetails.widget.codeBlockTitles.phpComposer")}
        />
      ),
    },
    {
      label: t("boardsDetails.widget.languages.python"),
      content: (
        <CodeBlock
          code={pythonCode}
          language="python"
          title={t("boardsDetails.widget.codeBlockTitles.pythonPip")}
        />
      ),
    },
    {
      label: t("boardsDetails.widget.languages.csharp"),
      content: (
        <CodeBlock
          code={csharpCode}
          language="csharp"
          title={t("boardsDetails.widget.codeBlockTitles.csharpNuget")}
        />
      ),
    },
  ];

  return (
    <MainContainer
      topbar={
        <div>
          <h2>{t("boardsDetails.widget.header")}</h2>
        </div>
      }
    >
      <GridBoard>
        <ContentBox>
          <IntroText>{t("boardsDetails.widget.intro")}</IntroText>

          <WidgetStep>
            <StepTitle>{t("boardsDetails.widget.step1.title")}</StepTitle>
            <StepDescription>
              {t("boardsDetails.widget.step1.description")}
            </StepDescription>
            <CodeBlock
              code={scriptCode}
              language="html"
              title={t("boardsDetails.widget.codeBlockTitles.htmlScript")}
            />
          </WidgetStep>

          <WidgetStep>
            <StepTitle>{t("boardsDetails.widget.step2.title")}</StepTitle>
            <StepDescription>
              {t("boardsDetails.widget.step2.description")}
            </StepDescription>
            <p
              style={{
                fontSize: "0.875rem",
                color: "#dc2626",
                fontWeight: "600",
                marginBottom: "1rem",
              }}
            >
              {t("boardsDetails.widget.replaceTokenWarning", {
                ssoToken: t("boardsDetails.widget.ssoToken"),
              })}
            </p>
            <CodeBlock
              code={htmlCode}
              language="html"
              title={t("boardsDetails.widget.codeBlockTitles.htmlWidget")}
            />
          </WidgetStep>

          <WidgetStep>
            <StepTitle>{t("boardsDetails.widget.step3.title")}</StepTitle>
            <StepDescription>
              {t("boardsDetails.widget.step3.description")}
            </StepDescription>
            <p
              style={{
                fontSize: "0.875rem",
                color: "#6b7280",
                fontStyle: "italic",
                marginBottom: "1rem",
              }}
            >
              {t("boardsDetails.widget.step3.note")}
            </p>
            <Tabs tabs={ssoTabs} defaultTab={0} />
          </WidgetStep>
        </ContentBox>
      </GridBoard>
    </MainContainer>
  );
}

export default BoardWidget;
